<template>
  <v-dialog
    :dialog="dialog"
    max-width="500px"
    transition="dialog-top-transition"
    persistent
  >
    <v-card rounded="xl">
      <v-card-title class="d-flex justify-space-between align-center">
        <div class="text-medium-emphasis ps-2">
          {{ isEdit ? "Edit " : "Add New " }}User
        </div>
        <v-btn icon="mdi-close" variant="text" @click="closeDialog()"></v-btn>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text style="overflow-y:auto;">
        <v-form ref="form" v-model="valid">
          <v-text-field
            label="First Name"
            :variant="fieldVariant"
            v-model="first_name"
            :rules="RULES.REQUIRED_FIELD('First Name')"
            :density="fieldDensity"
            :color="fieldColor"
          ></v-text-field>

          <v-text-field
            label="Last Name"
            :variant="fieldVariant"
            v-model="last_name"
            :rules="RULES.REQUIRED_FIELD('Last Name')"
            :density="fieldDensity"
            :color="fieldColor"
          ></v-text-field>

          <v-text-field
            :variant="fieldVariant"
            label="User Name"
            v-model="username"
            :rules="usernameRules"
            :density="fieldDensity"
            :color="fieldColor"
          ></v-text-field>

          <v-autocomplete
             label="ROLE"
             :items="['Member','Admin','Claims Specialist']"
             :variant="fieldVariant"
             :density="fieldDensity"
             v-model="role"
          ></v-autocomplete>

          <v-autocomplete
             label="User Group"
             :items="userGroups"
             :variant="fieldVariant"
             :density="fieldDensity"
             v-model="user_group_name"
          ></v-autocomplete>

          <v-text-field
            :variant="fieldVariant"
            label="Email"
            v-model="email"
            :rules="emailRules"
            :density="fieldDensity"
            :color="fieldColor"
            type="email"
          ></v-text-field>

          <v-text-field
            v-if="!isEdit"
            :variant="fieldVariant"
            label="Set Password"
            v-model="password"
            :rules="passwordRules"
            :density="fieldDensity"
            :color="fieldColor"
            autocomplete="new-password"
            :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
            :type="showPassword ? 'text' : 'password'"
            @click:append="showPassword = !showPassword"
          ></v-text-field>

      </v-form>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-btn color="red darken-1" text @click="closeDialog()">Cancel</v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="green"
          @click="handleSubmit"
          :disabled="!valid"
          variant="flat"
          rounded="xl"
          >{{ isEdit ? "Update" : "Add" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { RULES } from "@/constants/validationRules.js";
import AdminService from "@/services/adminService";
import EventBus from "@/eventBus";
import {ALERT} from "../../constants/alert.js"
import { mapGetters } from "vuex";
export default {
    data(){
        return{
            RULES,
            first_name: '',
            last_name: '',
            username: '',
            email: '',
            password: '',
            role: 'Member',
            valid: true,
            fieldVariant: 'underlined',
            fieldDensity: 'comfortable',
            fieldColor: 'primary',
            showPassword : false,
            originalUserName: '',
            user_group_name: 'ALL USERS',
            userGroups: ['ALL USERS']

        }
    },
    computed:{
        ...mapGetters("people",['users']),
        emailRules(){
            return [
                v => !!v || 'E-mail is required',
                v => /.+@.+\..+/.test(v) || 'E-mail must be valid',
            ]
        },
        passwordRules(){
            return [
                v => !!v || 'Password is required',
                v => v.length >= 8 || 'Password must be at least 8 characters',
            ]
        },
        usernameRules(){
             return [
          v => !!v || 'User Name is required',
          v => this.isEdit ? !this.isDuplicateUsername(v) || 'This user name is already taken' : !this.isDuplicateUsername(v) || 'This user name is already taken'
        ];
        }
    },
    props: {
        dialog: { type: Boolean, default: false },
        isEdit: { type: Boolean, default: false },
        selectedUser: { type: Object, default: null },
    },
    methods: {
        async handleSubmit() {
            const { valid } = await this.$refs.form.validate();
            if (!valid) {
                return;
            }
            let userData = {
                first_name: this.first_name,
                last_name: this.last_name,
                username: this.username,
                email: this.email,
                role: this.role,
                user_group_name: this.user_group_name,
            }
            if(!this.isEdit){
                userData.password = this.password;
            }
            if(this.isEdit){
                try {
                    let response = await AdminService.updateUser(userData, this.selectedUser.user_id);
                    if(response.status !== 200){
                        EventBus.$emit("alert", {message: "Failed to update user",type: ALERT.ERROR});
                    }
                } catch (error) {
                    console.error(error);
                }
            }else{
                try {
                    let response = await AdminService.createUser(userData);
                    if(response.status !== 201){
                       EventBus.$emit("alert", {message: "Failed to create user",type: ALERT.ERROR});
                    }
                    EventBus.$emit('refreshClientEndpoints');
                } catch (error) {
                    console.error(error);
                }
            }
            this.closeDialog();
        },
        closeDialog() {
            this.$emit('closeDialog');
        },
        isDuplicateUsername(name){
             return name?.toLowerCase().trim() !== this.originalUserName?.toLowerCase().trim() && 
          this.users.find((u)=> u.username.toLowerCase() === name.toLowerCase().trim());
        }
    },
    async created(){
        // Fetch user groups for the dropdown
        try {
            const response = await AdminService.getUserGroups(this.$store.getters.clientId);
            this.userGroups = response.data;
        } catch (error) {
            console.error('Error fetching user groups:', error);
        }
        
        if(this.isEdit && this.selectedUser){
            this.first_name = this.selectedUser.first_name;
            this.last_name = this.selectedUser.last_name;
            this.username = this.selectedUser.username;
            this.email = this.selectedUser.email;
            this.role = this.selectedUser.role;
            this.originalUserName = this.selectedUser.username;
            this.user_group_name = this.selectedUser.user_group_name || 'ALL USERS';
        }
    }
}
</script>

<style>

</style>